/**
 * @file Shared cross-cutting JS helpers.
 */

/**
 * Wrap a function so it runs at most once per animation frame.
 *
 * Ideal for high-frequency events that trigger layout reads/writes
 * (resize, scroll): calls arriving within the same frame collapse into a
 * single invocation, avoiding layout thrash. The wrapper always runs with
 * the arguments of the most recent call.
 *
 * @param {Function} fn
 *   The function to throttle.
 * @returns {Function}
 *   The throttled wrapper.
 */
export function rafThrottle(fn) {
  let frame = null;
  let lastArgs = null;
  return (...args) => {
    lastArgs = args;
    if (frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      fn(...lastArgs);
    });
  };
}

/**
 * Returns a debounced wrapper around `fn` that postpones invocation until
 * `wait` ms have elapsed since the last call. Use for listeners that fire
 * rapidly (input, scroll, resize).
 *
 * @param {Function} fn - The function to debounce.
 * @param {number} wait - Delay in milliseconds.
 * @returns {Function} The debounced function.
 */
export function debounce(fn, wait) {
  let timer;
  return function debounced(...args) {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => fn.apply(this, args), wait);
  };
}

/**
 * Returns a document-unique id with the given prefix.
 *
 * ARIA wiring needs ids — `aria-controls`, `aria-activedescendant`,
 * `aria-describedby` all reference one — so any component building a listbox or
 * a hint has to mint them. Each such component used to keep its own counter at
 * module scope; one counter shared across the theme cannot collide even if two
 * components ever pick the same prefix.
 *
 * @param {string} prefix - Prefix describing what the element is.
 * @returns {string} The id, e.g. "search-ac-3".
 */
let idCounter = 0;
export function uniqueId(prefix) {
  idCounter += 1;
  return `${prefix}-${idCounter}`;
}

/**
 * Returns whether a destination address points off this site.
 *
 * Compared by hostname rather than by origin, so an address that differs from
 * the current page only in scheme or port — an `http://` link to this same
 * site, a link written against a different local port — is still internal.
 * Anything that is not a web address (`mailto:`, `tel:`, a bare fragment) is
 * never external: it does not take the visitor to another site.
 *
 * @param {string} url - An absolute or relative URL.
 * @returns {boolean} TRUE when the address leaves this site.
 */
export function isExternalUrl(url) {
  try {
    const target = new URL(url, window.location.href);
    if (target.protocol !== "https:" && target.protocol !== "http:") {
      return false;
    }
    return target.hostname !== window.location.hostname;
  } catch {
    return false;
  }
}

/**
 * Returns the lower-cased file extension a URL's path ends in, or null.
 *
 * Read off the path rather than from a `Content-Type`, which would mean a
 * request per link. That makes it a guess, but a cheap and reliable one here:
 * Drupal stores an uploaded file under its own name, and an address that
 * carries no extension — a `/document/<id>` redirect, say — returns null
 * rather than being guessed at.
 *
 * Query strings and fragments are ignored: a file URL commonly carries an
 * `?itok=` and the extension is a property of the path.
 *
 * @param {string} url - An absolute or relative URL.
 * @returns {string|null} The extension without its dot, e.g. "pdf".
 */
export function fileExtensionFromUrl(url) {
  try {
    const target = new URL(url, window.location.href);
    if (target.protocol !== "https:" && target.protocol !== "http:") {
      return null;
    }
    const match = target.pathname.match(/\.([a-z0-9]+)$/i);
    return match ? match[1].toLowerCase() : null;
  } catch {
    return null;
  }
}

/**
 * Returns a link's target when the markup chose one, and null otherwise.
 *
 * This codebase writes "no target" three different ways, and only one of them
 * is an absent attribute: btn-base.twig prints `target="{{ link.target }}"`
 * unconditionally, so a link with nothing to say carries `target=""`, and
 * LinkitUrlExtension::buildButton() defaults a button's target to `_self`.
 * Neither is a decision about the link — the text formats expose no target
 * attribute to editors at all (`editor_advanced_link_link` enables none, and
 * `filter_html` allows neither `target` nor `rel`), so nothing on this site can
 * author `_self` on purpose.
 *
 * Read `hasAttribute("target")` instead and every button-rendered link looks
 * deliberate, which is what left the PDF buttons on /curricular-teaching-
 * resources opening over the page.
 *
 * @param {HTMLAnchorElement} link - The link to read.
 * @returns {string|null} The target, or null when the markup set none.
 */
export function deliberateTarget(link) {
  const target = (link.getAttribute("target") || "").trim();
  return target === "" || target === "_self" ? null : target;
}

/**
 * Reads a cookie value by name.
 *
 * @param {string} name - The cookie name.
 * @returns {string|null} The decoded value, or null if the cookie is absent.
 */
export function getCookie(name) {
  const escaped = name.replace(/([.*+?^${}()|[\]\\])/g, "\\$1");
  const match = document.cookie.match(
    new RegExp("(?:^|;\\s*)" + escaped + "=([^;]*)"),
  );
  return match ? decodeURIComponent(match[1]) : null;
}
